import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import {
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HereSpeedLimitCell } from "./components/HereSpeedLimitCell";
import { MarkDoneDialog } from "./components/MarkDoneDialog";
import { useSpeedLimitReports } from "./hooks/useSpeedLimitReports";
import { useUpdateSpeedLimitReport } from "./hooks/useUpdateSpeedLimitReport";
import { SpeedLimitReportDetails } from "./models/SpeedLimitReportData";
import { I18nGrid } from "@/components/data/I18nGrid";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { CastedAddInData } from "@/hooks/addins/useAddinData";
import { getUserDisplayName } from "@/utilities/user/getUserDisplayName";
import { createWorkbook, downloadBlob } from "@/utilities/xlsx";

type ReportRow = CastedAddInData<SpeedLimitReportDetails>;

type FilterMode = "open" | "all";

function Toolbar({
  filterMode,
  onFilterModeChange,
  onRefresh,
  isRefreshing,
  onExport,
  exportDisabled,
}: {
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  onExport: () => void;
  exportDisabled: boolean;
}) {
  const { t } = useTranslation();

  return (
    <GridToolbarContainer sx={{ gap: 1, px: 1 }}>
      <Tooltip title={t("speedLimitReport.refresh")}>
        <IconButton
          size="small"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label={t("speedLimitReport.refresh")}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t("speedLimitReport.exportExcel")}>
        <span>
          <IconButton
            size="small"
            onClick={onExport}
            disabled={exportDisabled}
            aria-label={t("speedLimitReport.exportExcel")}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <ToggleButtonGroup
        size="small"
        value={filterMode}
        exclusive
        onChange={(_, value) => {
          if (value !== null) onFilterModeChange(value);
        }}
        aria-label={t("speedLimitReport.filterLabel")}
      >
        <ToggleButton value="open" aria-label={t("speedLimitReport.openOnly")}>
          {t("speedLimitReport.openOnly")}
        </ToggleButton>
        <ToggleButton value="all" aria-label={t("speedLimitReport.showAll")}>
          {t("speedLimitReport.showAll")}
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter debounceMs={300} />
    </GridToolbarContainer>
  );
}

export default function SpeedLimitReportPage() {
  const { t } = useTranslation();
  const { me } = useGeotabClient();
  const { data: reports, isLoading, isRefetching, refetch } = useSpeedLimitReports();
  const updateReport = useUpdateSpeedLimitReport();
  const [filterMode, setFilterMode] = useState<FilterMode>("open");
  const [doneDialogRow, setDoneDialogRow] = useState<ReportRow | null>(null);

  const userName = me ? getUserDisplayName(me) : "";

  const filteredReports = useMemo(
    () => (filterMode === "open" ? reports.filter((r) => !r.details.done) : reports),
    [reports, filterMode],
  );

  const handleToggleDone = useCallback(
    (row: ReportRow) => {
      if (row.details.done) {
        updateReport.mutate({
          recordId: row.id,
          details: {
            ...row.details,
            done: false,
            doneBy: undefined,
            doneAt: undefined,
            processorComment: undefined,
          },
        });
      } else {
        setDoneDialogRow(row);
      }
    },
    [updateReport],
  );

  const handleConfirmDone = useCallback(
    (comment: string, speedAdjusted: boolean) => {
      if (!doneDialogRow) return;
      updateReport.mutate({
        recordId: doneDialogRow.id,
        details: {
          ...doneDialogRow.details,
          done: true,
          doneBy: userName,
          doneAt: new Date().toISOString(),
          processorComment: comment || undefined,
          speedAdjusted,
        },
      });
      setDoneDialogRow(null);
    },
    [doneDialogRow, updateReport, userName],
  );

  const handleExport = useCallback(() => {
    const headers: string[] = [
      t("speedLimitReport.done"),
      t("speedLimitReport.doneBy"),
      t("speedLimitReport.speedAdjustedColumn"),
      t("speedLimitReport.processorComment"),
      t("speedLimitReport.reportedAt"),
      t("speedLimitReport.driver"),
      t("speedLimitReport.vehicle"),
      t("speedLimitReport.address"),
      t("speedLimitReport.displayedLimit"),
      t("speedLimitReport.correctLimit"),
      t("speedLimitReport.comment"),
    ];
    const rows: string[][] = filteredReports.map((r) => {
      const d = r.details as unknown as SpeedLimitReportDetails;
      const num = Number(d.correctLimit);
      const correctLimitStr = Number.isFinite(num)
        ? `${d.correctLimit} km/h`
        : t("speedLimitReport.noSpeedLimit");
      return [
        d.done ? t("yes") : t("no"),
        d.doneBy ?? "",
        d.done ? (d.speedAdjusted ? t("yes") : t("no")) : "",
        d.processorComment ?? "",
        new Date(d.reportedAt).toLocaleString(),
        d.driverName,
        d.deviceName,
        d.address,
        `${d.displayedLimit} km/h`,
        correctLimitStr,
        d.comment,
      ];
    });
    const blob = createWorkbook([headers, ...rows], { headerRow: 1 });
    downloadBlob(blob, `tempolimit-meldungen-${new Date().toISOString().split("T")[0]}.xlsx`);
  }, [filteredReports, t]);

  const columns: GridColDef<ReportRow>[] = useMemo(
    () => [
      {
        field: "done",
        headerName: t("speedLimitReport.done"),
        width: 70,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.details.done ?? false,
        renderCell: (params) => (
          <Tooltip
            title={
              params.row.details.done
                ? t("speedLimitReport.markOpen")
                : t("speedLimitReport.markDone")
            }
          >
            <IconButton
              size="small"
              onClick={() => handleToggleDone(params.row)}
              aria-label={
                params.row.details.done
                  ? t("speedLimitReport.markOpen")
                  : t("speedLimitReport.markDone")
              }
            >
              {params.row.details.done ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <CircleOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "doneBy",
        headerName: t("speedLimitReport.doneBy"),
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.details.doneBy ?? "",
      },
      {
        field: "speedAdjusted",
        headerName: t("speedLimitReport.speedAdjustedColumn"),
        type: "boolean",
        width: 130,
        valueGetter: (_value, row) => {
          // Only meaningful on done reports — leave blank otherwise.
          if (!row.details.done) return null;
          return row.details.speedAdjusted ?? false;
        },
        renderCell: (params) => {
          if (params.value === null) return null;
          return params.value ? t("yes") : t("no");
        },
      },
      {
        field: "processorComment",
        headerName: t("speedLimitReport.processorComment"),
        flex: 1.5,
        minWidth: 150,
        valueGetter: (_value, row) => row.details.processorComment ?? "",
      },
      {
        field: "reportedAt",
        headerName: t("speedLimitReport.reportedAt"),
        flex: 1,
        minWidth: 160,
        type: "dateTime",
        valueGetter: (_value, row) => new Date(row.details.reportedAt),
      },
      {
        field: "driverName",
        headerName: t("speedLimitReport.driver"),
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.details.driverName,
      },
      {
        field: "deviceName",
        headerName: t("speedLimitReport.vehicle"),
        flex: 1,
        minWidth: 130,
        valueGetter: (_value, row) => row.details.deviceName,
      },
      {
        field: "address",
        headerName: t("speedLimitReport.address"),
        flex: 1.5,
        minWidth: 180,
        valueGetter: (_value, row) => row.details.address,
      },
      {
        field: "displayedLimit",
        headerName: t("speedLimitReport.displayedLimit"),
        width: 150,
        type: "number",
        valueGetter: (_value, row) => row.details.displayedLimit,
        valueFormatter: (value: number) => `${value} km/h`,
      },
      {
        field: "hereLimit",
        headerName: t("speedLimitReport.hereLimit"),
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => <HereSpeedLimitCell details={params.row.details} />,
      },
      {
        field: "correctLimit",
        headerName: t("speedLimitReport.correctLimit"),
        width: 150,
        valueGetter: (_value, row) => row.details.correctLimit,
        valueFormatter: (value: string) => {
          if (!value) return "";
          const num = Number(value);
          return Number.isFinite(num) ? `${value} km/h` : t("speedLimitReport.noSpeedLimit");
        },
      },
      {
        field: "comment",
        headerName: t("speedLimitReport.comment"),
        flex: 1.5,
        minWidth: 150,
        valueGetter: (_value, row) => row.details.comment,
      },
      {
        field: "exceptionUrl",
        headerName: t("speedLimitReport.geotabLink"),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title={t("speedLimitReport.geotabLink")}>
            <IconButton
              size="small"
              href={params.row.details.exceptionUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("speedLimitReport.geotabLink")}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [t, handleToggleDone],
  );

  return (
    <Stack sx={{ height: "100%", p: 2, gap: 2 }}>
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <MarkDoneDialog
          open={doneDialogRow !== null}
          userName={userName}
          onClose={() => setDoneDialogRow(null)}
          onConfirm={handleConfirmDone}
        />
        <I18nGrid
          rows={filteredReports}
          columns={columns}
          loading={isLoading || isRefetching}
          getRowId={(row) => row.id}
          density="compact"
          initialState={{
            sorting: { sortModel: [{ field: "reportedAt", sort: "desc" }] },
          }}
          disableRowSelectionOnClick
          slots={{ toolbar: Toolbar as unknown as React.JSXElementConstructor<object> }}
          slotProps={{
            toolbar: {
              filterMode,
              onFilterModeChange: setFilterMode,
              onRefresh: refetch,
              isRefreshing: isRefetching,
              onExport: handleExport,
              exportDisabled: filteredReports.length === 0,
            } as any,
          }}
        />
      </Box>
    </Stack>
  );
}
